import React from 'react';
import type { InsightsResponse, InsightsRange } from '../../../../shared/lib/insights';
import { ActivityHeatmap } from './ActivityHeatmap';

/**
 * Forge Wrapped card — the playful stats surface (headline tiles + activity
 * heatmap + fun fact). Used full-size on /insights and compact on Today.
 */

const RANGES: { id: InsightsRange; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: '30d', label: '30d' },
  { id: '7d', label: '7d' },
];

const Tile: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({ label, value, sub }) => (
  <div className="bg-slate-100/70 rounded-xl px-4 py-3">
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-lg font-semibold text-slate-900 tabular-nums truncate" title={typeof value === 'string' ? value : undefined}>
      {value}
    </div>
    {sub && <div className="text-2xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

function hourLabel(h: number | null): string {
  if (h === null) return '—';
  const am = h < 12;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12} ${am ? 'AM' : 'PM'}`;
}
const dash = (v: number | null, fmt: (n: number) => string): string => (v === null ? '—' : fmt(v));

export const InsightsCard: React.FC<{
  data: InsightsResponse;
  range: InsightsRange;
  onRange: (r: InsightsRange) => void;
  /** compact = Today teaser (fewer tiles, no agents table). */
  compact?: boolean;
  /** UX-02 — hide the built-in range toggle when a parent (the /insights
   *  hub's Overview tab) already drives `range` from a shared filter bar,
   *  so only one range control ever renders on the page. */
  hideRangeToggle?: boolean;
  /** UX-02 — small note next to the title, e.g. "(all time)" when the
   *  hub's 90d range got mapped onto this card's "all" (its API only
   *  supports 7d/30d/all). */
  subtitle?: React.ReactNode;
}> = ({ data, range, onRange, compact = false, hideRangeToggle = false, subtitle }) => {
  const t = data.tiles;
  return (
    <div data-testid="insights-card" className="bg-white border border-slate-200 rounded-2xl shadow-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">
          Forge Wrapped
          {subtitle && <span className="text-xs font-normal text-slate-400 ml-2">{subtitle}</span>}
        </h2>
        {!hideRangeToggle && (
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5" role="radiogroup" aria-label="Time range">
            {RANGES.map((r) => (
              <button
                key={r.id}
                role="radio"
                aria-checked={range === r.id}
                data-testid={`insights-range-${r.id}`}
                type="button"
                onClick={() => onRange(r.id)}
                className={
                  'px-3 py-1 text-xs rounded-md font-medium transition-colors ' +
                  (range === r.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800')
                }
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Headline tiles — Efficiency + Favorites/streaks. */}
      <div className={'grid gap-2.5 ' + (compact ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-4')}>
        <Tile label="Avg cost / story-point" value={dash(t.avgCostPerStoryPoint, (n) => `$${n.toFixed(2)}`)} />
        <Tile label="Success rate" value={dash(t.successRatePct, (n) => `${n}%`)} />
        {!compact && <Tile label="Avg turnaround" value={dash(t.avgTurnaroundHours, (n) => (n < 48 ? `${n}h` : `${(n / 24).toFixed(1)}d`))} />}
        <Tile label="Favorite agent" value={t.favoriteAgent?.type ?? '—'} sub={t.favoriteAgent ? `${t.favoriteAgent.count} runs` : undefined} />
        {!compact && <Tile label="Favorite model" value={t.favoriteModel?.id ?? '—'} sub={t.favoriteModel ? `${t.favoriteModel.count} runs` : undefined} />}
        <Tile label="Most active repo" value={t.mostActiveRepo?.repo.split('/').pop() ?? '—'} sub={t.mostActiveRepo?.repo} />
        <Tile label="Current streak" value={`${t.currentStreakDays}d`} sub={`longest ${t.longestStreakDays}d`} />
        {!compact && <Tile label="Peak hour" value={hourLabel(t.peakHour)} />}
      </div>

      {!compact && <ActivityHeatmap cells={data.heatmap} />}

      <p className="text-sm text-slate-500">{data.funFact}</p>

      {/* FORGE-303 (P1-1) — estimation accuracy tile. Renders nothing when no
          epic has a completed quote-vs-actual delta yet (cold start / no
          quotes on this account). Full view only, mirrors the agents table. */}
      {!compact && data.costAccuracy.sampleSize > 0 && (
        <div className="pt-1" data-testid="estimation-accuracy">
          <div className="text-xs font-medium text-slate-600 mb-1.5">
            Estimation accuracy <span className="text-slate-400 font-normal">({data.costAccuracy.sampleSize} epic{data.costAccuracy.sampleSize === 1 ? '' : 's'})</span>
          </div>
          <div className="grid gap-2.5 grid-cols-2 sm:grid-cols-4">
            <Tile label="Within band" value={dash(data.costAccuracy.withinBandRatePct, (n) => `${n}%`)} />
            <Tile label="Median |error|" value={dash(data.costAccuracy.medianAbsPctError, (n) => `${n}%`)} />
            <Tile
              label="Median band width"
              value={dash(data.costAccuracy.medianBandWidthPct, (n) => `±${n}%`)}
              sub={
                data.costAccuracy.bandWidthTrend === 'narrowing' ? 'tightening ↓'
                : data.costAccuracy.bandWidthTrend === 'widening' ? 'widening ↑'
                : data.costAccuracy.bandWidthTrend === 'flat' ? 'steady'
                : undefined
              }
            />
            <Tile label="Calibrated (vs fleet)" value={dash(data.costAccuracy.calibratedSharePct, (n) => `${n}%`)} />
          </div>
        </div>
      )}

      {!compact && data.agents.length > 0 && (
        <div className="pt-1">
          <div className="text-xs font-medium text-slate-600 mb-1.5">Agents</div>
          <div className="space-y-1">
            {data.agents.slice(0, 8).map((a) => {
              const max = data.agents[0]?.runs || 1;
              return (
                <div key={a.agentType} className="flex items-center gap-2 text-xs">
                  <span className="w-32 shrink-0 truncate text-slate-700">{a.agentType}</span>
                  <div className="flex-1 bg-slate-100 rounded h-2 overflow-hidden">
                    <div className="bg-primary-400 h-full rounded" style={{ width: `${(a.runs / max) * 100}%` }} />
                  </div>
                  <span className="w-12 text-right tabular-nums text-slate-600">{a.runs}</span>
                  <span className="w-16 text-right tabular-nums text-slate-500">${a.costUsd.toFixed(2)}</span>
                  <span className="w-12 text-right tabular-nums text-slate-400">{a.failureRatePct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsCard;
